import { type NotificationContent } from 'primary-schema';

import { type Common } from './common.js';
import type * as Broadcast from './models/broadcast.js';
import { type OffsetPaginationResponse } from './models/common.js';
import { type UpdateValues } from './models/notification/content.js';
import type * as NotificationRequest from './models/notification/request.js';
import { type PaginatedQueryOptions, type PaginationResponse } from './models/pagination/index.js';
import { type ImportValidation } from './models/provider.js';

export class API {
  common: Common;
  baseEndpoint: string;

  constructor(common: Common) {
    this.common = common;
    this.baseEndpoint = '/broadcasts';
  }

  async sendWithEncounters(
    args: Broadcast.EncounterBroadcastArgs
  ): Promise<Broadcast.BroadcastPopulated> {
    const url = this.common.constructPath(this.baseEndpoint);
    const { broadcast } = await this.common.postJSON(url, args);
    return broadcast;
  }

  async batchSendWithEncounters(
    args: Broadcast.EncounterBatchBroadcastArgs
  ): Promise<Broadcast.BatchBroadcastResult> {
    const url = this.common.constructPath(`${this.baseEndpoint}/batch-encounter`);
    return await this.common.postJSON(url, args);
  }

  async findAllPaginated(
    options: PaginatedQueryOptions<Broadcast.FindAllOptions>
  ): Promise<PaginationResponse<Broadcast.FindAllResponse>> {
    const url = this.common.constructPath(this.baseEndpoint, {
      ...options,
    });
    return await this.common.getJSON(url);
  }

  async template(
    values: Broadcast.BroadcastBodyValues
  ): Promise<Broadcast.BroadcastTemplateContents> {
    const url = this.common.constructPath(`${this.baseEndpoint}/template`);
    return await this.common.postJSON(url, values);
  }

  async updateTemplate(id: string, values: UpdateValues): Promise<NotificationContent[]> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${id}`);
    return await this.common.patchJSON(url, values);
  }

  async removeTemplate(id: string): Promise<{ success: true }> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${id}`);
    return await this.common.deleteJSON(url);
  }

  async findOneById(id: string): Promise<Broadcast.BroadcastPopulated> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${id}`);
    return await this.common.getJSON(url);
  }

  async findAllResults(
    args: NotificationRequest.FindAllOptions & {
      broadcastId: string;
    }
  ): Promise<OffsetPaginationResponse<NotificationRequest.GroupedResult>> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${args.broadcastId}/requests`, {
      ...args,
      broadcastId: undefined,
    });
    return await this.common.getJSON(url);
  }

  async import(args: Broadcast.BroadcastDataArgs): Promise<boolean> {
    const url = this.common.constructPath(`${this.baseEndpoint}/data`);
    const { success } = await this.common.postJSON(url, args);
    return success;
  }

  async importVerify(args: {
    practiceId: string;
    importRows: Record<string, string>[];
  }): Promise<Record<string, ImportValidation>[]> {
    const url = this.common.constructPath(`${this.baseEndpoint}/data/verify`);
    return await this.common.postJSON(url, args);
  }

  async processBroadcastData(): Promise<boolean> {
    const url = this.common.constructPath(`${this.baseEndpoint}/data/process`);
    const { success } = await this.common.postJSON(url, {});
    return success;
  }

  async processNotificationRequest(): Promise<boolean> {
    const url = this.common.constructPath(`/notification-requests/process`);
    const { success } = await this.common.postJSON(url, {});
    return success;
  }
}
